/**
 * First-party cookie-consent store.
 *
 * Consent is persisted in localStorage under `aa_consent` as either
 * "granted" or "denied". A missing value means the visitor has not chosen
 * yet (the banner should be shown).
 *
 * Defaults differ by tracker. Meta Pixel and PostHog stay off until the
 * visitor accepts. Google's tag is on by default for US visitors and off
 * until accepted in the EEA, UK and Switzerland (see the Consent Mode
 * defaults in app/layout.tsx). Reject turns all of them off.
 *
 * Global Privacy Control counts as Reject for a visitor who has not answered
 * the banner. An explicit Accept on this site still wins, because it is the
 * more specific instruction; Reject and GPC agree, so there is nothing to
 * resolve there.
 *
 * Every helper is SSR-safe — on the server (`typeof window === "undefined"`)
 * reads return null and writes no-op.
 */

export type ConsentValue = "granted" | "denied";

export const CONSENT_STORAGE_KEY = "aa_consent";
export const CONSENT_CHANGE_EVENT = "aa-consent-change";
export const CONSENT_OPEN_EVENT = "aa-consent-open";

/** Reopen the consent banner (used by the footer "Cookie Preferences" link). */
export function openConsentBanner(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(CONSENT_OPEN_EVENT));
}

/** True when the browser is sending the Global Privacy Control signal. */
export function gpcEnabled(): boolean {
  if (typeof navigator === "undefined") return false;
  return (navigator as Navigator & { globalPrivacyControl?: boolean })
    .globalPrivacyControl === true;
}

export function getConsent(): ConsentValue | null {
  if (typeof window === "undefined") return null;
  let value: string | null = null;
  try {
    value = window.localStorage.getItem(CONSENT_STORAGE_KEY);
  } catch {
    // localStorage can throw (private mode, disabled cookies) — treat as unset.
  }
  if (value === "granted" || value === "denied") return value;
  // No answer yet, but the browser already said no.
  return gpcEnabled() ? "denied" : null;
}

export function setConsent(value: ConsentValue): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, value);
  } catch {
    // Ignore write failures — the live gating in the banner still runs.
  }
  window.dispatchEvent(
    new CustomEvent<ConsentValue>(CONSENT_CHANGE_EVENT, { detail: value }),
  );
}

/**
 * Subscribe to consent changes. Returns an unsubscribe function. The callback
 * receives the new value. No-op on the server.
 */
export function onConsentChange(
  callback: (value: ConsentValue) => void,
): () => void {
  if (typeof window === "undefined") return () => {};
  const handler = (event: Event) => {
    const detail = (event as CustomEvent<ConsentValue>).detail;
    if (detail === "granted" || detail === "denied") callback(detail);
  };
  window.addEventListener(CONSENT_CHANGE_EVENT, handler);
  return () => window.removeEventListener(CONSENT_CHANGE_EVENT, handler);
}
